import React, { useState, useEffect, useRef, useMemo } from "react";
import { FaTimes, FaRobot } from "react-icons/fa";
import { useTypedSelector } from "@/hooks";
import {
  useFetchAISuggestionMessagesQuery,
  usePostCompaRatioReviewMutation,
  usePostCriticalPayPositioningMutation,
  usePostRetentionRiskMutation,
  usePostHireConversionAnalysisMutation,
  useOneTimePaymentMutation,
  usePayTrendBasePayMutation,
  usePayTrendTotalTargetCashMutation,
  useRolesInDemandMutation,
} from "@/services/aisuggestion";
import { getSelectedCompanyFromStorage } from "@/services/cookies";
import { useFetchOfferCurrencyRateQuery } from "@/services";

export function AISuggestionBot() {
  const offerData = useTypedSelector(state => state.offers);

  const auth = useTypedSelector(state => state.auth);

  const messagesEndRef = useRef(null);
  const debounceRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const [isAIReady, setIsAIReady] = useState(false);

  const [initialParams, setInitialParams] = useState(null);
  const [initialSetupDone, setInitialSetupDone] = useState(false);

  const [rolesTriggered, setRolesTriggered] = useState(false);
  const [conversionTriggered, setConversionTriggered] = useState(false);
  const [initialPostsDone, setInitialPostsDone] = useState(false);

  const company_id = getSelectedCompanyFromStorage() || auth?.user?.company?.id;
  const offer_id = offerData?.offer?.id;

  const { data: countryList } = useFetchOfferCurrencyRateQuery(
    {
      code: offerData?.positionDetails?.currency_id,
      // code: offerData?.offer?.offer_candidate_detail?.currency?.id,
      company_id,
      offer_id,
    },
    {
      skip: !company_id || !offer_id,
      refetchOnMountOrArgChange: true,
    }
  );

  const fixed = offerData?.offerModeller?.offer_fixed_cash;
  const sti = offerData?.offerModeller?.offer_sti;
  const lti = offerData?.offerModeller?.offer_lti;

  const proposed_annual_base = fixed?.proposed_annual_base ?? 0;
  const compa_ratio = fixed?.proposed_compa_ratio ?? 0;
  const market_ratio = fixed?.proposed_market_ratio ?? 0;

  const current_unvested_equity = lti?.current_unvested_equity ?? 0;

  const proposed_guaranteed_cash = fixed?.proposed_total ?? 0;
  const proposed_total_target_cash = useMemo(() => {
    return (sti?.proposed_total_bonus || 0) + (fixed?.proposed_total || 0);
  }, [sti, fixed]);

  const result = countryList?.data?.candidate_currency?.result || {};
  const key = Object.keys(result)[0];
  const value = key ? result[key] : 0;

  const current_guaranteed_cash = value * fixed?.current_total;
  const current_total_target_cash =
    value * ((fixed?.current_total || 0) + (sti?.current_total_bonus || 0));

  const params = {
    compa_ratio,
    market_ratio,
    proposed_annual_base,
    current_total_target_cash,
    proposed_total_target_cash,
    current_unvested_equity,
    current_guaranteed_cash,
    proposed_guaranteed_cash,
  };

  const {
    data: insightsData,
    isFetching: isFetchingInsights,
    refetch: refetchInsights,
  } = useFetchAISuggestionMessagesQuery(
    { company_id, offer_id },
    { skip: !company_id || !offer_id || !initialPostsDone }
  );

  const [postCompa, { isLoading: isCompaLoading }] =
    usePostCompaRatioReviewMutation();
  const [postCritical, { isLoading: isCriticalLoading }] =
    usePostCriticalPayPositioningMutation();
  const [postRetention, { isLoading: isRetentionLoading }] =
    usePostRetentionRiskMutation();
  const [postConversion, { isLoading: isConversionLoading }] =
    usePostHireConversionAnalysisMutation();
  const [postOneTime, { isLoading: isOneTimeLoading }] =
    useOneTimePaymentMutation();
  const [postBasePay, { isLoading: isBasePayLoading }] =
    usePayTrendBasePayMutation();
  const [postTtcTrend, { isLoading: isTtcLoading }] =
    usePayTrendTotalTargetCashMutation();
  const [postRoles, { isLoading: isRolesLoading }] = useRolesInDemandMutation();

  const isTyping =
    isFetchingInsights ||
    isCompaLoading ||
    isCriticalLoading ||
    isRetentionLoading ||
    isConversionLoading ||
    isOneTimeLoading ||
    isBasePayLoading ||
    isTtcLoading ||
    isRolesLoading;

  const formatTime = isoString => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  /* ================= INITIAL API SEQUENCE FIX ================= */

  useEffect(() => {
    if (!company_id || !offer_id || initialPostsDone) return;

    const runInitialApis = async () => {
      try {
        // Trigger both mutations but wait for both to finish
        const rolesResult = postRoles({ company_id, offer_id }).unwrap();
        const conversionResult = postConversion({
          company_id,
          offer_id,
        }).unwrap();

        // Wait for both promises to resolve
        await Promise.all([rolesResult, conversionResult]);

        setRolesTriggered(true);
        setConversionTriggered(true);

        setInitialPostsDone(true); // Allow insights to fetch now

        // Fetch insights after both mutations have completed
        refetchInsights();
      } catch (error) {
        console.error("Error during initial API calls:", error);
        setInitialPostsDone(true); // Fail-safe to allow insights to fetch even on failure
      }
    };

    runInitialApis();
  }, [company_id, offer_id, initialPostsDone]);

  /* ================= INSIGHTS LOAD ================= */

  useEffect(() => {
    if (!insightsData?.data?.insights) return;

    const mapped = insightsData.data.insights
      .map(i => ({
        id: i.id,
        field: i.alert_type?.replace(/_/g, " "),
        content: i.message,
        timestamp: i.created_at,
      }))
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    setMessages(mapped);

    if (!isAIReady) setIsAIReady(true);
    else if (!isOpen) setHasNew(true);

    if (!initialSetupDone) {
      setInitialParams(params);
      setInitialSetupDone(true);
    }
  }, [insightsData]);

  /* ================= WATCH CHANGES (UNCHANGED) ================= */

  useEffect(() => {
    if (!initialSetupDone || !initialParams) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      let nextBaseline = { ...initialParams };
      let anyApiCalled = false;

      if (
        compa_ratio !== initialParams.compa_ratio ||
        market_ratio !== initialParams.market_ratio
      ) {
        await Promise.all([
          postCompa({ company_id, offer_id, compa_ratio, market_ratio }),
          postCritical({ company_id, offer_id, compa_ratio, market_ratio }),
        ]);
        nextBaseline.compa_ratio = compa_ratio;
        nextBaseline.market_ratio = market_ratio;
        anyApiCalled = true;
      }

      if (proposed_annual_base !== initialParams.proposed_annual_base) {
        await postBasePay({
          company_id,
          offer_id,
          proposed_annual_base_pay: proposed_annual_base,
        });
        nextBaseline.proposed_annual_base = proposed_annual_base;
        anyApiCalled = true;
      }

      if (
        proposed_total_target_cash !== initialParams.proposed_total_target_cash
      ) {
        await postTtcTrend({
          company_id,
          offer_id,
          total_target_cash: proposed_total_target_cash,
        });
        nextBaseline.proposed_total_target_cash = proposed_total_target_cash;
        anyApiCalled = true;
      }

      if (
        proposed_annual_base > 0 &&
        (proposed_total_target_cash !==
          initialParams.proposed_total_target_cash ||
          current_unvested_equity !== initialParams.current_unvested_equity)
      ) {
        await postOneTime({
          company_id,
          offer_id,
          current_total_target_cash,
          proposed_total_target_cash,
          current_unvested_equity,
        });
        nextBaseline.current_unvested_equity = current_unvested_equity;
        nextBaseline.current_total_target_cash = current_total_target_cash;
        anyApiCalled = true;
      }

      if (
        proposed_annual_base > 0 &&
        (current_total_target_cash !==
          initialParams.current_total_target_cash ||
          proposed_total_target_cash !==
            initialParams.proposed_total_target_cash ||
          current_guaranteed_cash !== initialParams.current_guaranteed_cash ||
          proposed_guaranteed_cash !== initialParams.proposed_guaranteed_cash)
      ) {
        await postRetention({
          company_id,
          offer_id,
          current_total_target_cash,
          proposed_total_target_cash,
          current_guaranteed_cash,
          proposed_guaranteed_cash,
        });
        nextBaseline.current_total_target_cash = current_total_target_cash;
        nextBaseline.current_guaranteed_cash = current_guaranteed_cash;
        nextBaseline.proposed_guaranteed_cash = proposed_guaranteed_cash;
        anyApiCalled = true;
      }

      if (anyApiCalled) {
        setInitialParams(nextBaseline);
        refetchInsights();
      }
    }, 700);

    return () => clearTimeout(debounceRef.current);
  }, [
    compa_ratio,
    market_ratio,
    proposed_annual_base,
    proposed_total_target_cash,
    current_total_target_cash,
    current_unvested_equity,
    current_guaranteed_cash,
    proposed_guaranteed_cash,
    initialParams,
    initialSetupDone,
    company_id,
    offer_id,
  ]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping, isOpen]);

  useEffect(() => {
    if (!offer_id) return;

    setMessages([]);
    setIsOpen(false);
    setHasNew(false);
    setIsAIReady(false);
    setInitialParams(null);
    setInitialSetupDone(false);
    setRolesTriggered(false);
    setConversionTriggered(false);
    setInitialPostsDone(false);
  }, [offer_id]);

  return (
    <div className="fixed bottom-6 right-6 z-[99999] flex flex-col items-end gap-3">
      {isOpen && (
        <div className="w-96 h-96 bg-white dark:bg-slate-900 rounded-2xl shadow-xl border overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
            <h3 className="font-semibold text-sm text-white">AI Suggestions</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/10 p-1 rounded-lg"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>

          {insightsData?.data?.insights?.length > 0 ? (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map(msg => (
                <div key={msg.id}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs font-bold">
                      AI
                    </div>
                    <span className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                      {msg.field}
                    </span>
                  </div>

                  <div className="ml-8 p-3 bg-white dark:bg-slate-800 border rounded-xl text-sm">
                    <span className="block">{msg.content}</span>
                    <span className="block text-xs text-gray-400 mr-auto text-right">
                      {formatTime(msg.timestamp)}
                    </span>
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="ml-8 flex gap-1">
                  <span className="w-2 h-2 bg-indigo-300 rounded-full animate-bounce"></span>
                  <span className="w-2 h-2 bg-indigo-300 rounded-full animate-bounce delay-150"></span>
                  <span className="w-2 h-2 bg-indigo-300 rounded-full animate-bounce delay-300"></span>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <p className="border rounded-lg p-2 text-gray-400">No Messages</p>
            </div>
          )}
        </div>
      )}

      <button
        disabled={!isAIReady}
        onClick={() => {
          if (!isAIReady) return;
          setIsOpen(!isOpen);
          setHasNew(false);
        }}
        className={`relative w-16 h-16 rounded-full flex items-center justify-center shadow-xl transition
          ${
            isAIReady
              ? "bg-indigo-600 hover:scale-105 cursor-pointer"
              : "bg-slate-300 cursor-not-allowed opacity-60"
          }
        `}
      >
        <FaRobot
          className={`w-7 h-7 ${isAIReady ? "text-white" : "text-slate-500"}`}
        />

        {hasNew && !isOpen && isAIReady && (
          <span className="absolute top-2 right-2 w-3 h-3 rounded-full bg-red-500 animate-ping" />
        )}
      </button>
    </div>
  );
}
